import React from 'react';
import Link from 'next/link';
import { useMutation } from '@apollo/client';
import { Button } from '@avila-tek/ui/src';
import { ClientGoals, Company, ShopCart, User } from '../../../models';
import { ADD_CLIENT_GOAL_REWARDS } from '../../../graphql/mutation';
import { useNotify, useShopCart, useUser } from '../../../hooks';
import { Badge } from '../../shared';
import LoadingButton from '../../shared/LoadingButton';

interface AddToShopCartBtnProps {
  clientGoals: ClientGoals;
  benefitId: string;
  preferenceBenefits: number;
  setPreferenceBenefits: React.Dispatch<React.SetStateAction<number>>;
  preferenceBenefitPrice: number;
  refreshGoals?: () => Promise<void>;
  userId?: string;
}

function AddToShopCartBtn({
  clientGoals,
  benefitId,
  preferenceBenefits,
  setPreferenceBenefits,
  preferenceBenefitPrice,
  refreshGoals,
  userId,
}: AddToShopCartBtnProps) {
  const notify = useNotify();
  const [user] = useUser();
  const [_, setShopCart] = useShopCart();

  const [loading, setLoading] = React.useState(false);

  const [addClientGoalRewardsMutation] = useMutation<{
    addClientGoalRewards: ShopCart;
  }>(ADD_CLIENT_GOAL_REWARDS);

  const addToShopCart = async () => {
    try {
      if (loading) return;
      setLoading(true);

      if (!benefitId) return notify('Seleccione un beneficio', 'error');

      const { rewarded } = clientGoals;

      // check if preferenceBenefits is enough
      const benefitsWithDiscount =
        preferenceBenefits >= rewarded
          ? preferenceBenefits - rewarded
          : preferenceBenefits;

      const dataBenefits = [];

      for (let index = 0; index < rewarded; index += 1) {
        dataBenefits.push({
          benefitPrice:
            index < benefitsWithDiscount ? preferenceBenefitPrice : 5,
        });
      }

      setPreferenceBenefits(preferenceBenefits - benefitsWithDiscount);

      const { data } = await addClientGoalRewardsMutation({
        variables: {
          data: {
            clientId: (clientGoals.user as User)?._id,
            clientGoalsId: clientGoals?._id,
            shopCartId: (user.company as Company)?.shopCart?._id,
            benefitId,
            benefitPrice: dataBenefits.map((benefit) => benefit.benefitPrice),
          },
        },
      });
      if (data?.addClientGoalRewards?._id) {
        setShopCart(data?.addClientGoalRewards);
        notify('Agregado al carrito', 'success');
        if (refreshGoals) await refreshGoals();
      }
    } catch (error) {
      notify(error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex space-x-2 items-center">
      {clientGoals.rewarded === 0 ? (
        <Badge
          bgClassName="bg-purple-200"
          textClassName="text-white"
          text="Meta no alcanzada"
        />
      ) : null}
      {clientGoals.rewarded === clientGoals.alreadyRewarded &&
      clientGoals.rewarded !== 0 ? (
        <Badge
          bgClassName="bg-purple-200"
          textClassName="text-white"
          text="Meta pagada"
        />
      ) : null}
      {clientGoals.rewarded !== 0 &&
      clientGoals.rewarded !== clientGoals.alreadyRewarded ? (
        <LoadingButton
          onClick={() => addToShopCart()}
          loading={loading}
          text="Agregar al carrito"
          className="relative z-50"
        />
      ) : null}
      <Button className="bg-white border border-purple-300 text-purple-300">
        <Link href={`/app/employees/${userId}/${clientGoals._id}`}>
          {clientGoals.rewarded === clientGoals.alreadyRewarded
            ? 'Detalles'
            : 'Editar'}
        </Link>
      </Button>
    </div>
  );
}

export default AddToShopCartBtn;
